import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CreditCard, Coins, ExternalLink, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


export default function Billing() {
    const { user, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [portalBusy, setPortalBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const d = await api.billingStatus();
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load billing");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openPortal = async () => {
        setPortalBusy(true);
        try {
            const { url } = await api.billingPortal();
            window.location.href = url;
        } catch (e) {
            toast.error(e.message || "Portal error");
        } finally {
            setPortalBusy(false);
        }
    };

    return (
        <SidebarLayout>
            <div className="max-w-4xl mx-auto py-8 px-4">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold">Billing</h1>
                        <p className="text-sm text-text-secondary">
                            Piano corrente, crediti, storico invoice.
                        </p>
                    </div>
                    <button
                        data-testid="billing-refresh"
                        onClick={load}
                        className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs text-text-secondary hover:text-text-main hover:border-text-secondary/40"
                    >
                        <RefreshCw className="h-3 w-3" /> Refresh
                    </button>
                </div>

                {loading ? (
                    <p className="text-text-secondary text-sm">Loading…</p>
                ) : (
                    <>
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="billing-plan-card"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                        >
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-lg font-semibold">
                                            {data?.plan?.name || "Free"}
                                        </h2>
                                        {data?.plan_status && (
                                            <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[var(--color-secondary)]">
                                                {data.plan_status.replace(/_/g, " ")}
                                            </span>
                                        )}
                                        {data?.cancel_at_period_end && (
                                            <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[var(--color-alert)]">
                                                Cancel pending
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-text-secondary">
                                        {data?.plan?.credits_per_month?.toLocaleString() || 0}{" "}
                                        crediti/mese · cap{" "}
                                        {data?.plan?.hourly_credit_cap?.toLocaleString() || 0}/ora
                                    </p>
                                    {data?.plan_current_period_end && (
                                        <p className="mt-2 text-xs text-text-secondary">
                                            Prossimo rinnovo:{" "}
                                            {new Date(data.plan_current_period_end).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    {user?.plan_id !== "free" && data?.plan_status && (
                                        <button
                                            data-testid="billing-portal-btn"
                                            onClick={openPortal}
                                            disabled={portalBusy}
                                            className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] px-4 py-2 text-xs font-medium text-black hover:opacity-90 disabled:opacity-50"
                                        >
                                            <CreditCard className="h-3.5 w-3.5" />
                                            {portalBusy ? "Opening…" : "Manage subscription"}
                                        </button>
                                    )}
                                    <Link
                                        to="/app/subscription"
                                        data-testid="billing-view-pricing"
                                        className="inline-flex items-center justify-center gap-1 rounded-full border border-[var(--color-border)] px-4 py-2 text-xs text-text-secondary hover:text-text-main hover:border-text-secondary/40"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Change plan
                                    </Link>
                                </div>
                            </div>
                        </motion.div>

                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <StatCard
                                icon={<Coins className="h-4 w-4 text-[var(--color-secondary)]" />}
                                label="Crediti attuali"
                                value={data?.credits?.toLocaleString() || 0}
                                testid="billing-credits-balance"
                            />
                            <StatCard
                                icon={<CreditCard className="h-4 w-4 text-[var(--color-primary)]" />}
                                label="Reset previsto"
                                value={
                                    data?.credits_reset_at
                                        ? new Date(data.credits_reset_at).toLocaleDateString()
                                        : "—"
                                }
                            />
                        </div>

                        <div className="mt-8" data-testid="billing-invoices">
                            <h3 className="text-sm font-semibold uppercase tracking-widest text-text-secondary">
                                Invoice history
                            </h3>
                            <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                {(data?.invoices || []).length === 0 ? (
                                    <p className="p-6 text-sm text-text-secondary">
                                        Nessuna invoice ancora — attiva un piano per iniziare.
                                    </p>
                                ) : (
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                                <th className="px-4 py-3">Number</th>
                                                <th className="px-4 py-3">Status</th>
                                                <th className="px-4 py-3">Amount</th>
                                                <th className="px-4 py-3">Period</th>
                                                <th className="px-4 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.invoices.map((inv) => (
                                                <tr
                                                    key={inv.id}
                                                    className="border-b border-[var(--color-border)] last:border-0"
                                                >
                                                    <td className="px-4 py-3 tabular-nums">
                                                        {inv.number || inv.id.slice(-8)}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span
                                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                                                                inv.status === "paid"
                                                                    ? "border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                                                                    : "border border-[var(--color-border)] text-text-secondary"
                                                            }`}
                                                        >
                                                            {inv.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums">
                                                        ${inv.amount_paid_eur?.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary text-xs">
                                                        {inv.period_start
                                                            ? new Date(
                                                                  inv.period_start * 1000,
                                                              ).toLocaleDateString()
                                                            : "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {inv.hosted_invoice_url && (
                                                            <a
                                                                href={inv.hosted_invoice_url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-1 text-xs text-[var(--color-secondary)] hover:underline"
                                                            >
                                                                Open <ExternalLink className="h-3 w-3" />
                                                            </a>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </SidebarLayout>
    );
}

function StatCard({ icon, label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                {icon}
                {label}
            </div>
            <p className="mt-2 text-2xl font-semibold tabular-nums">{value}</p>
        </div>
    );
}
